import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { API, apiClient as axios } from "../../lib/api";
import { Toast } from "../../components/ui";
import { Button } from "../../components/ui";
import { RefreshCw, Plus, Loader2 } from "lucide-react";
import UsersTable from "./components/UsersTable";
import UserModal from "./components/UserModal";

export default function Users({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createdUserWithPassword, setCreatedUserWithPassword] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users`, { headers: authHeaders });
      setUsers(res.data?.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleRowClick = useCallback((user) => {
    setSelectedUser(user);
    setCreatedUserWithPassword(null);
    setModalOpen(true);
  }, []);

  const handleAddUser = () => {
    setSelectedUser(null);
    setCreatedUserWithPassword(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setCreatedUserWithPassword(null);
  };

  const handleSaveUser = async (userData) => {
    try {
      setSubmitting(true);

      if (selectedUser) {
        // Update existing user (status only for now)
        await axios.patch(
          `${API}/users/${selectedUser.id}/status`,
          { status: userData.status },
          { headers: authHeaders }
        );
        toast.success("User updated successfully!");
        setModalOpen(false);
        setSelectedUser(null);
        setCreatedUserWithPassword(null);
      } else {
        // Create new user
        const res = await axios.post(
          `${API}/users`,
          userData,
          { headers: authHeaders }
        );
        
        // Store the created user with temporary password to display in modal
        setCreatedUserWithPassword(res.data?.data);
        toast.success("User created successfully!");
        // Don't close modal yet - let admin see and copy the password
      }

      await loadUsers();
    } catch (e) {
      const errorMessage = e?.response?.data?.error || "Failed to save user";
      toast.error(errorMessage);
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <Toast />
      <div className="toolbar">
        <div>
          <div className="title">Users Management</div>
          <div className="subtitle">Manage user accounts and permissions</div>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <Button
            variant="outline"
            onClick={loadUsers}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </>
            )}
          </Button>
          <Button
            onClick={handleAddUser}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>
      <UsersTable
        loading={loading}
        users={users}
        onRowClick={handleRowClick}
      />
      <UserModal
        open={modalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        createdUserWithPassword={createdUserWithPassword}
        onSave={handleSaveUser}
        submitting={submitting}
      />
    </div>
  );
}

